import { useUploadStore } from "@components/FileUpload/uploadStore";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

interface UploadParams {
  files: File[];
  startIdx: number;
  certificateType: string;
  storeKey?: string;
}

const UPLOAD_SHIPPING_AGENCY_TRANSFER_DOCUMENT_TRANSACTION_CONTEXT = {
  module: "vessel_call",
  screen: "ShippingAgencyTransfer",
  action: "upload_shipping_agency_transfer_document",
  entityType: "shipping_agency_transfer_document",
};

export const useUploadShippingAgencyTransferDocuments = ({
  handleUploadSuccess,
  handleUploadError,
}: {
  handleUploadSuccess?: (data: {
    documentUrls: string[];
    startIdx: number;
    files: File[];
    certificateType: string;
    storeKey?: string;
  }) => void;
  handleUploadError?: (
    error: Error,
    meta: {
      startIdx: number;
      count: number;
      fileNames: string[];
      certificateType: string;
      storeKey?: string;
    },
  ) => void;
}) => {
  const mutation = useMutation({
    mutationFn: ({ files, startIdx, certificateType, storeKey }: UploadParams) =>
      runTransaction(
        UPLOAD_SHIPPING_AGENCY_TRANSFER_DOCUMENT_TRANSACTION_CONTEXT,
        async (transactionId) => {
          const documentUrls: string[] = [];
          for (const file of files) {
            const payloadForSasToken = {
              prefix: SasTokenPrefix.VESSEL_CALL,
              contentType: file.type || null,
              privilege: SasTokenPrivilege.WRITE,
              blobUrl: null,
            };
            const { blobUrl, sasToken } = await getSasToken(
              payloadForSasToken,
              ...(transactionId ? [transactionId] : []),
            );
            const result = await uploadFileToAzureBlob({
              blobUrl,
              sasToken,
              file,
              certificateType,
            });
            result.forEach((r) => documentUrls.push(r.documentUrl));
          }
          return { documentUrls, startIdx, files, certificateType, storeKey };
        },
      ),
    onMutate: ({ storeKey }) => {
      if (storeKey) {
        useUploadStore.getState().beginUploadInFlight(storeKey);
      }
    },
    onSuccess: (data) => handleUploadSuccess?.(data),
    onError: (error: Error, variables: UploadParams) => {
      handleUploadError?.(error, {
        startIdx: variables.startIdx,
        count: variables.files.length,
        fileNames: variables.files.map((f) => f.name),
        certificateType: variables.certificateType,
        storeKey: variables.storeKey,
      });
    },
    onSettled: (_data, _error, variables) => {
      if (variables?.storeKey) {
        useUploadStore.getState().endUploadInFlight(variables.storeKey);
      }
    },
  });

  return {
    ...mutation,
  };
};
